import type { Anchors } from "../lib/api.js";

/** What Undo move is called and says (g102). Exported so the tests pin the words. */
export const MOVE_UNDO = {
  label: "Undo move",
  short: "Undo",
  moved: "Moved.",
  undone: "Move undone.",
} as const;

/**
 * How a move went (g102), so nothing is said about one that did not happen.
 *
 * `applied` is on screen and being written to this device; `saved` says
 * whether that write took. `deferred` is waiting for a link's visitor to say
 * who they are. `missing` had no row to move, and `refused` was not allowed or
 * failed, which the screen has already said.
 */
export type MoveResult =
  | { status: "applied"; saved: Promise<boolean> }
  | { status: "deferred" }
  | { status: "missing" }
  | { status: "refused" };

/**
 * The row the last drop moved, and the neighbours it had before.
 *
 * `operationId` is the id that drop was queued under (g108), where it was
 * queued as an action the server records. Undo move gives it back, so history
 * holds the undo as a new move that names the one it put back. It is a link
 * and nothing more: whether the undo may still run is decided as before.
 */
export interface MoveTarget {
  id: string;
  back: Anchors;
  operationId?: string;
}

/** What a screen passes the store for a move: the move an undo puts back, and where to keep a drop's own id. */
export function moveLink(reverses: string | undefined, made: (operationId: string) => void): { reverses?: string; made(operationId: string): void } {
  return { ...(reverses !== undefined ? { reverses } : {}), made };
}

/**
 * A mounted screen's one Undo move: the latest drop this device has saved, until it is undone, a
 * later move replaces or clears it, its row goes or the screen is left. It is
 * held by the screen, not by the message that reported the drop, so it
 * outlasts that message and Reorder mode's Done.
 *
 * This is the early form of undo (g102): one drag, on the screen it was made
 * on, lost on a reload. Steps by arrow key and menu are not offered, and
 * there is no redo.
 */
export function moveTarget(changed: (target: MoveTarget | undefined) => void) {
  let target: MoveTarget | undefined;
  return {
    get: () => target,
    set(next: MoveTarget | undefined): void {
      if (next === target) return;
      target = next;
      changed(target);
    },
  };
}
